import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/nextjs";
import { useAccount, useCurrentUser } from "@zapier/client";
import { createContext, useEffect } from "react";
import { setGrafanaFaroUser } from "./faro";

interface DatadogRumUserProviderProps {
  children: React.ReactNode;
}

interface UserInfo {
  user: ReturnType<typeof useCurrentUser>["user"];
  account: ReturnType<typeof useAccount>["account"];
}

const userInfoContext = createContext<UserInfo | undefined>(undefined);

export function DatadogRumUserProvider(props: DatadogRumUserProviderProps) {
  const { user, isLoading: userIsLoading } = useCurrentUser();
  const { account, isLoading: accountIsLoading } = useAccount();

  const userId = user?.id;
  const userEmail = user?.email;
  const userFirstName = user?.firstName;
  const userLastName = user?.lastName;
  const userPlanName = account?.planInfo?.name;
  const accountId = account?.id;

  useEffect(() => {
    if (userIsLoading || accountIsLoading || !userId || !accountId) {
      setGrafanaFaroUser();
      return;
    }

    setGrafanaFaroUser({
      id: String(userId),
      accountId: String(accountId),
    });

    return () => setGrafanaFaroUser();
  }, [userIsLoading, accountIsLoading, userId, accountId]);

  // Sync user data with Datadog and Sentry
  useEffect(() => {
    if (userIsLoading || accountIsLoading) {
      return;
    }

    if (!userId || !accountId) {
      return;
    }

    datadogRum.setUser({
      email: userEmail ?? undefined,
      id: userId?.toString() ?? undefined,
      name: userFirstName + " " + userLastName,
      userPlan: userPlanName,
    });

    Sentry.setUser({
      accountId: accountId?.toString() ?? undefined,
      id: userId?.toString() ?? undefined,
    });
  }, [
    userIsLoading,
    accountIsLoading,
    userId,
    userEmail,
    userFirstName,
    userLastName,
    userPlanName,
    accountId,
  ]);

  const contextValue: UserInfo | undefined =
    user && account ? { user, account } : undefined;

  return (
    <userInfoContext.Provider value={contextValue}>
      {props.children}
    </userInfoContext.Provider>
  );
}
